import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMonitor, FiSmartphone } from 'react-icons/fi';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import useGSAPScrollAnimations from '../hooks/useGSAPScrollAnimations';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

const Screenshots = () => {
  const [activeDevice, setActiveDevice] = useState('desktop');
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const titleRef = useRef(null);
  const descriptionRef = useRef(null);
  const deviceSelectorRef = useRef(null);
  const screenshotRef = useRef(null);
  const featuresRef = useRef(null);
  
  const { fadeInUp, fadeInLeft, fadeInRight, scaleIn, staggerIn, parallax } = useGSAPScrollAnimations();

  useEffect(() => {
    // Animate section elements on scroll
    if (headerRef.current) {
      fadeInUp(headerRef.current, { delay: 0.1 });
    }
    
    if (titleRef.current) {
      // Text reveal animation for title
      const title = titleRef.current;
      gsap.fromTo(title.children, 
        { 
          y: 50, 
          opacity: 0,
          rotationX: 90 
        },
        { 
          y: 0, 
          opacity: 1,
          rotationX: 0,
          duration: 0.8,
          stagger: 0.1,
          ease: "back.out(1.7)",
          scrollTrigger: {
            trigger: title,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        }
      );
    }
    
    if (descriptionRef.current) {
      fadeInUp(descriptionRef.current, { delay: 0.2 });
    }
    
    if (deviceSelectorRef.current) {
      scaleIn(deviceSelectorRef.current, { delay: 0.3 });
    }
    
    if (screenshotRef.current) {
      // Complex animation for screenshot container
      gsap.fromTo(screenshotRef.current,
        {
          y: 100,
          opacity: 0,
          scale: 0.8,
          rotationY: 15
        },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          rotationY: 0,
          duration: 1.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: screenshotRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse"
          }
        }
      );
    }
    
    if (featuresRef.current) {
      const featureCards = featuresRef.current.children;
      staggerIn(Array.from(featureCards), { 
        stagger: 0.15,
        y: 50,
        delay: 0.4 
      });
    }

    // Parallax effect for background elements
    const backgroundElements = sectionRef.current?.querySelectorAll('.parallax-bg');
    if (backgroundElements) {
      backgroundElements.forEach((element, index) => {
        parallax(element, { 
          yPercent: index % 2 === 0 ? -30 : 30,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top bottom",
            end: "bottom top",
            scrub: 1
          }
        });
      });
    }

    // Floating elements animation
    const floatingElements = sectionRef.current?.querySelectorAll('.floating-element');
    if (floatingElements) {
      floatingElements.forEach((element, index) => {
        gsap.to(element, {
          y: index % 2 === 0 ? -20 : 20,
          rotation: index % 2 === 0 ? 5 : -5,
          duration: 3 + index,
          repeat: -1,
          yoyo: true,
          ease: "power2.inOut",
          delay: index * 0.5
        });
      });
    }

    // Mouse follow effect
    const handleMouseMove = (e) => {
      const rect = sectionRef.current?.getBoundingClientRect();
      if (rect) {
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        
        gsap.to('.mouse-follow', {
          x: x * 20,
          y: y * 20,
          duration: 0.5,
          ease: "power2.out"
        });
      }
    };

    const section = sectionRef.current;
    if (section) {
      section.addEventListener('mousemove', handleMouseMove);
      return () => section.removeEventListener('mousemove', handleMouseMove);
    }
  }, [fadeInUp, fadeInLeft, fadeInRight, scaleIn, staggerIn, parallax]);

  const screenshots = {
    desktop: {
      src: "data:image/svg+xml,%3Csvg viewBox='0 0 1200 800' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='mainBg' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23f8fafc'/%3E%3Cstop offset='100%25' style='stop-color:%23e2e8f0'/%3E%3C/linearGradient%3E%3ClinearGradient id='sidebarBg' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23f1f5f9'/%3E%3Cstop offset='100%25' style='stop-color:%23e2e8f0'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='1200' height='800' fill='url(%23mainBg)'/%3E%3Crect x='0' y='0' width='300' height='800' fill='url(%23sidebarBg)'/%3E%3Cg%3E%3Crect x='20' y='20' width='50' height='50' rx='12' fill='%23a855f7'/%3E%3Ctext x='45' y='50' text-anchor='middle' fill='white' font-size='24' font-family='Inter' font-weight='bold'%3EB%3C/text%3E%3Ctext x='85' y='45' fill='%23374151' font-size='18' font-family='Inter' font-weight='bold'%3EBerVin%3C/text%3E%3C/g%3E%3Crect x='20' y='100' width='260' height='40' rx='8' fill='%23ffffff' stroke='%23d1d5db'/%3E%3Ctext x='30' y='125' fill='%239ca3af' font-size='14' font-family='Inter'%3E🔍 Search%3C/text%3E%3Cg%3E%3Crect x='20' y='160' width='260' height='50' rx='8' fill='%23ffffff' stroke='%23d1d5db'/%3E%3Ctext x='30' y='180' fill='%23374151' font-size='12' font-family='Inter' font-weight='bold'%3ENew Chat%3C/text%3E%3Ctext x='30' y='200' fill='%236b7280' font-size='11' font-family='Inter'%3EStart a conversation%3C/text%3E%3C/g%3E%3Cg%3E%3Crect x='20' y='220' width='260' height='50' rx='8' fill='%23f3f4f6'/%3E%3Ctext x='30' y='240' fill='%23374151' font-size='12' font-family='Inter' font-weight='bold'%3EMulti-AI Discussion%3C/text%3E%3Ctext x='30' y='255' fill='%236b7280' font-size='11' font-family='Inter'%3EComparing AI responses...%3C/text%3E%3C/g%3E%3Cg%3E%3Crect x='20' y='280' width='260' height='50' rx='8' fill='%23ffffff'/%3E%3Ctext x='30' y='300' fill='%23374151' font-size='12' font-family='Inter' font-weight='bold'%3ECode Review%3C/text%3E%3Ctext x='30' y='315' fill='%236b7280' font-size='11' font-family='Inter'%3EPython optimization tips%3C/text%3E%3C/g%3E%3Crect x='320' y='20' width='860' height='60' rx='12' fill='%23ffffff' stroke='%23e5e7eb'/%3E%3Ctext x='340' y='45' fill='%23374151' font-size='16' font-family='Inter' font-weight='bold'%3E💬 Multi-AI Chat%3C/text%3E%3Ctext x='340' y='65' fill='%236b7280' font-size='12' font-family='Inter'%3E🔥 gemini-2.5-flash • GPT-4 • Claude-3%3C/text%3E%3Crect x='340' y='120' width='820' height='580' rx='12' fill='%23ffffff' stroke='%23e5e7eb'/%3E%3Ctext x='750' y='400' text-anchor='middle' fill='%239ca3af' font-size='20' font-family='Inter'%3EHow can I help you today%3F%3C/text%3E%3Crect x='340' y='720' width='820' height='60' rx='12' fill='%23f9fafb' stroke='%23e5e7eb'/%3E%3Ctext x='360' y='755' fill='%239ca3af' font-size='16' font-family='Inter'%3EAsk me anything...%3C/text%3E%3Ccircle cx='1120' cy='750' r='15' fill='%23a855f7'/%3E%3Ctext x='1120' y='755' text-anchor='middle' fill='white' font-size='12' font-family='Inter'%3E▶%3C/text%3E%3C/svg%3E",
      alt: "Bervin Desktop Application - Multi-AI Chat Interface",
      width: "1200",
      height: "800"
    },
    mobile: {
      src: "data:image/svg+xml,%3Csvg viewBox='0 0 320 640' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3ClinearGradient id='mobileBg' x1='0%25' y1='0%25' x2='100%25' y2='100%25'%3E%3Cstop offset='0%25' style='stop-color:%23ffffff'/%3E%3Cstop offset='100%25' style='stop-color:%23f8fafc'/%3E%3C/linearGradient%3E%3C/defs%3E%3Crect width='320' height='640' fill='url(%23mobileBg)' rx='24'/%3E%3Cg%3E%3Ctext x='20' y='35' fill='%23000000' font-size='12' font-family='Inter' font-weight='500'%3E6:23%3C/text%3E%3Cg transform='translate(250,25)'%3E%3Crect width='15' height='8' rx='1' fill='none' stroke='%23000000' stroke-width='1'/%3E%3Crect x='18' y='2' width='1.5' height='4' rx='0.5' fill='%23000000'/%3E%3Cpath d='M25 3 L28 1 L31 3 L28 5 Z' fill='%23000000'/%3E%3Cpath d='M35 2 L38 2 M35 4 L38 4' stroke='%23000000' stroke-width='1'/%3E%3Crect x='42' y='1' width='6' height='8' rx='1' fill='%23000000'/%3E%3C/g%3E%3C/g%3E%3Cg%3E%3Crect x='15' y='60' width='25' height='25' rx='6' fill='%23ffffff' stroke='%23e5e7eb'/%3E%3Ctext x='27' y='77' text-anchor='middle' fill='%23374151' font-size='10' font-family='Inter'%3E📱%3C/text%3E%3Ctext x='50' y='75' fill='%23374151' font-size='14' font-family='Inter' font-weight='500'%3ESingle%3C/text%3E%3Crect x='120' y='65' width='85' height='20' rx='10' fill='%236b46c1'/%3E%3Ctext x='125' y='77' fill='%23ffffff' font-size='10' font-family='Inter' font-weight='500'%3E⚡ gemini-2.5-flash%3C/text%3E%3Crect x='215' y='65' width='20' height='20' rx='10' fill='%23ef4444'/%3E%3Ctext x='225' y='77' text-anchor='middle' fill='%23ffffff' font-size='8' font-family='Inter'%3E⬇%3C/text%3E%3Ctext x='240' y='77' fill='%23ef4444' font-size='10' font-family='Inter' font-weight='500'%3E9%3C/text%3E%3Crect x='270' y='65' width='25' height='20' rx='3' fill='%23f3f4f6'/%3E%3Ctext x='282' y='77' text-anchor='middle' fill='%23374151' font-size='12'%3E⋮%3C/text%3E%3C/g%3E%3Ctext x='160' y='320' text-anchor='middle' fill='%239ca3af' font-size='16' font-family='Inter'%3EHow can I help you today%3F%3C/text%3E%3Crect x='15' y='560' width='290' height='40' rx='10' fill='%23ffffff' stroke='%23e5e7eb'/%3E%3Ctext x='25' y='585' fill='%239ca3af' font-size='14' font-family='Inter'%3EAsk me anything...%3C/text%3E%3Cg%3E%3Crect x='25' y='615' width='20' height='20' rx='3' fill='%23f3f4f6'/%3E%3Cpath d='M30 620 L38 625 L30 630 Z' fill='%23374151'/%3E%3Crect x='55' y='615' width='20' height='20' rx='10' fill='%23374151'/%3E%3Ccircle cx='65' cy='625' r='2' fill='%23ffffff'/%3E%3Crect x='60' y='628' width='10' height='2' fill='%23ffffff'/%3E%3Crect x='85' y='615' width='20' height='20' rx='3' fill='%23f3f4f6'/%3E%3Ctext x='95' y='628' text-anchor='middle' fill='%23374151' font-size='14'%3E%2B%3C/text%3E%3Crect x='115' y='615' width='20' height='20' rx='3' fill='%23f3f4f6'/%3E%3Cpath d='M120 620 L125 625 L130 620 M125 620 L125 630' stroke='%23374151' stroke-width='1.5' fill='none'/%3E%3Ccircle cx='285' cy='625' r='6' fill='%23374151'/%3E%3Ctext x='285' y='628' text-anchor='middle' fill='%23ffffff' font-size='8'%3E%3F%3C/text%3E%3C/g%3E%3C/svg%3E",
      alt: "Bervin Mobile Application - AI Chat Interface",
      width: "320",
      height: "640"
    }
  };

  const devices = [
    { id: 'desktop', icon: FiMonitor, label: 'Desktop' },
    { id: 'mobile', icon: FiSmartphone, label: 'Mobile' },
  ];

  return (
    <section 
      ref={sectionRef}
      id="screenshots" 
      className="py-12 sm:py-16 lg:py-24 px-4 sm:px-6 relative overflow-hidden"
    >
      {/* Full blur background overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/40 via-purple-900/20 to-cyan-900/30" 
           style={{ 
             backdropFilter: 'blur(100px)',
             WebkitBackdropFilter: 'blur(100px)'
           }}></div>
      
      <div className="container mx-auto relative z-10">
        {/* Background Elements with Parallax */}
        <div className="absolute inset-0 opacity-30">
          <div
            className="parallax-bg absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-secondary-500/20 to-accent-500/20 rounded-full mouse-follow"
            style={{ 
              filter: 'blur(120px)',
              WebkitFilter: 'blur(120px)'
            }}
          />
          <div
            className="parallax-bg absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-r from-accent-500/20 to-primary-500/20 rounded-full mouse-follow"
            style={{ 
              filter: 'blur(100px)',
              WebkitFilter: 'blur(100px)'
            }}
          />
        </div>

        {/* Header */}
        <div
          ref={headerRef}
          className="text-center mb-8 sm:mb-12 lg:mb-16 relative z-10"
        >
          <h2
            ref={titleRef}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-4 sm:mb-6 px-4"
          >
            <span className="gradient-text block">Beautiful</span>
            <span className="gradient-text block">Interface</span>
          </h2>
          <p
            ref={descriptionRef}
            className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto px-4"
          >
            Experience Bervin across all your devices with our sleek, intuitive design
          </p>
        </div>

        {/* Device Selector */}
        <div
          ref={deviceSelectorRef}
          className="flex justify-center mb-8 sm:mb-12 relative z-10 px-4"
        >
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 p-2 rounded-2xl border border-white/10 w-full sm:w-auto max-w-md sm:max-w-none" 
               style={{ 
                 background: 'rgba(255, 255, 255, 0.05)', 
                 backdropFilter: 'blur(40px)',
                 WebkitBackdropFilter: 'blur(40px)'
               }}>
            {devices.map((device) => (
              <motion.button
                key={device.id}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveDevice(device.id)}
                className={`flex items-center justify-center sm:justify-start space-x-2 px-4 sm:px-6 py-3 rounded-xl transition-all duration-300 text-sm sm:text-base ${
                  activeDevice === device.id
                    ? 'bg-gradient-to-r from-secondary-500 to-accent-500 text-white shadow-lg'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                <device.icon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="font-medium">{device.label}</span>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Screenshot Display */}
        <div
          ref={screenshotRef}
          className="relative flex justify-center items-center min-h-[300px] sm:min-h-[400px] lg:min-h-[600px] px-2 sm:px-4"
        >
          {/* Floating Frame */}
          <div className="relative w-auto">
            {/* Background Glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-secondary-500/30 to-accent-500/30 rounded-xl scale-105"
                 style={{ 
                   filter: 'blur(60px)',
                   WebkitFilter: 'blur(60px)'
                 }}></div>
            
            {/* Device Frame */}
            <motion.div
              key={activeDevice}
              initial={{ opacity: 0, rotateY: 90 }}
              animate={{ opacity: 1, rotateY: 0 }}
              exit={{ opacity: 0, rotateY: -90 }}
              transition={{ duration: 0.3 }}
              className="relative border border-white/20 rounded-lg sm:rounded-xl shadow-2xl p-1 sm:p-2"
              style={{ 
                background: 'rgba(255, 255, 255, 0.03)', 
                backdropFilter: 'blur(60px)',
                WebkitBackdropFilter: 'blur(60px)',
                perspective: "1000px",
                width: 'fit-content'
              }}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeDevice}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2 }}
                  className="relative overflow-hidden rounded-lg sm:rounded-xl"
                >
                  <img
                    src={screenshots[activeDevice].src}
                    alt={screenshots[activeDevice].alt}
                    className="w-full h-auto shadow-xl block"
                    style={{
                      width: activeDevice === 'mobile' 
                        ? 'min(280px, 80vw)' 
                        : 'min(700px, 80vw)',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                  
                  {/* Screen Overlay Effect */}
                  <div className="absolute inset-0 bg-gradient-to-t from-transparent via-transparent to-white/5 pointer-events-none"></div>
                  
                  {/* Reflection Effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent opacity-30 pointer-events-none"></div>
                </motion.div>
              </AnimatePresence>

              {/* Device-specific decorations */}
              {activeDevice === 'desktop' && (
                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-8 sm:w-12 h-0.5 bg-gradient-to-r from-secondary-500/50 to-accent-500/50 rounded-full blur-sm"></div>
              )}
              
              {activeDevice === 'mobile' && (
                <>
                  <div className="absolute top-1 left-1/2 transform -translate-x-1/2 w-4 sm:w-6 h-0.5 bg-white/30 rounded-full"></div>
                  <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-4 sm:w-6 h-0.5 bg-white/30 rounded-full"></div>
                </>
              )}
            </motion.div>
          </div>
        </div>

        {/* Feature Highlights */}
        <div
          ref={featuresRef}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mt-12 sm:mt-16 relative z-10 px-4"
        >
          {[
            {
              title: "Responsive Design",
              description: "Seamlessly adapts to any screen size and device orientation",
              gradient: "from-secondary-500 to-secondary-600"
            },
            {
              title: "Touch Optimized",
              description: "Intuitive touch controls and gestures for mobile devices",
              gradient: "from-accent-500 to-accent-600"
            },
            {
              title: "Cross-Platform",
              description: "Consistent experience across all platforms and devices",
              gradient: "from-primary-500 to-primary-600"
            }
          ].map((feature, index) => (
            <div
              key={feature.title}
              className="text-center p-4 sm:p-6 border border-white/10 rounded-xl hover-lift"
              style={{ 
                background: 'rgba(255, 255, 255, 0.03)', 
                backdropFilter: 'blur(50px)',
                WebkitBackdropFilter: 'blur(50px)'
              }}
            >
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2 sm:mb-3">{feature.title}</h3>
              <p className="text-sm sm:text-base text-gray-300">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Screenshots;
